import { useState, useId, useRef, useEffect } from 'react';
import type { ReactNode, MouseEvent, ButtonHTMLAttributes } from 'react';
import { useKeyPressEvent, useMedia } from 'react-use';
import {
  useFloating,
  offset,
  flip,
  shift,
  arrow as arrowMiddleware,
  type Placement,
} from '@floating-ui/react';
import { getBreakpoint } from '../../Theme';
import { Overlay } from '../Overlay';
import { TooltipTrigger, TooltipContent, TooltipArrow } from './Tooltip.styles';

const TOOLTIP_OFFSET_PX = 16;

type TooltipProps = {
  title?: string;
  trigger: ReactNode;
  content: ReactNode;
  width?: 'default' | 'wide';
  position?: Placement;
} & Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label' | 'aria-labelledby' | 'className'
>;

const Tooltip = ({
  content,
  trigger,
  title,
  width = 'default',
  position = 'bottom',
  ...rest
}: TooltipProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const id = `tooltip-content-${useId()}`;
  const arrowRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles, middlewareData, placement, update } =
    useFloating({
      placement: position,
      strategy: 'fixed',
      middleware: [
        offset(({ placement: p }) =>
          p === 'left' || p === 'right'
            ? TOOLTIP_OFFSET_PX + 6
            : TOOLTIP_OFFSET_PX,
        ),
        flip({ fallbackPlacements: ['top', 'bottom'] }),
        shift({ padding: TOOLTIP_OFFSET_PX }),
        arrowMiddleware({ element: arrowRef }),
      ],
    });

  const isMobile = useMedia(`(max-width: ${getBreakpoint('lg')}px)`, false);

  // workaround to update tooltip position after layout change (e.g. feature toggle)
  useEffect(() => {
    if (showTooltip) {
      update();
    }
  }, [showTooltip, update]);

  useKeyPressEvent('Escape', () => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  });

  const handleTooltipToggle = (event?: MouseEvent) => {
    event?.stopPropagation();
    setShowTooltip(prev => !prev);
  };

  const handleTooltipVisibility = (visible: boolean) => {
    if (!isMobile) {
      setShowTooltip(visible);
    }
  };

  const arrowData = middlewareData.arrow;

  return (
    <div
      onMouseEnter={() => handleTooltipVisibility(true)}
      onMouseLeave={() => handleTooltipVisibility(false)}
    >
      <TooltipTrigger
        type="button"
        ref={refs.setReference as React.Ref<HTMLButtonElement>}
        aria-describedby={id}
        aria-haspopup={isMobile ? 'dialog' : undefined}
        onClick={handleTooltipToggle}
        onBlur={() => handleTooltipVisibility(false)}
        $hover={!isMobile}
        {...rest}
      >
        {trigger}
      </TooltipTrigger>

      {isMobile ? (
        <Overlay
          id={id}
          open={showTooltip}
          title={title}
          onClose={() => setShowTooltip(false)}
        >
          {content}
        </Overlay>
      ) : (
        <TooltipContent
          role="tooltip"
          id={id}
          $width={width}
          $visible={showTooltip}
          ref={refs.setFloating}
          style={floatingStyles}
          data-popper-placement={placement}
        >
          <TooltipArrow
            ref={arrowRef}
            style={{
              position: 'absolute',
              left: arrowData?.x != null ? `${arrowData.x}px` : '',
              top: arrowData?.y != null ? `${arrowData.y}px` : '',
            }}
          />
          {content}
        </TooltipContent>
      )}
    </div>
  );
};

export { Tooltip };
